<template>
  <section class="dealer-services">
    <div class="dealer-services__container">
      <!-- Left Column - Image -->
      <div class="dealer-services__image">
        <img
          :src="imageSrc"
          :alt="imageAlt"
          class="dealer-services__img" />
      </div>

      <!-- Right Column - Content -->
      <div class="dealer-services__content">
        <!-- Big Text -->
        <div class="dealer-services__title">
          <big-text
            :text="title"
            text-color="var(--color-black)" />
        </div>

        <!-- 77px Gap -->
        <div class="dealer-services__gap"></div>

        <!-- Service List -->
        <div class="dealer-services__list">
          <service-list
            :max-height="listMaxHeight"
            :single-column="singleColumn">
            <slot></slot>
          </service-list>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
  import { computed } from 'vue'
  import BigText from '../BigText.vue'
  import ServiceList from './ServiceList.vue'

  // Props
  const props = defineProps({
    title: {
      type: String,
      required: true,
      validator: value => typeof value === 'string' && value.trim().length > 0
    },
    imageSrc: {
      type: String,
      required: true
    },
    imageAlt: {
      type: String,
      default() {
        return useI18n().t('dealer.images.servicesImage')
      }
    },
    listMaxHeight: {
      type: String,
      default: '400px'
    },
    itemsCount: {
      type: Number,
      default: 0
    }
  })

  // Con pochi servizi le due colonne non si riempiono dentro la min-height
  // del blocco: sotto questa soglia la lista passa a colonna singola.
  const SINGLE_COLUMN_THRESHOLD = 4

  const singleColumn = computed(
    () => props.itemsCount > 0 && props.itemsCount <= SINGLE_COLUMN_THRESHOLD
  )
</script>

<style lang="scss" scoped>
.dealer-services {
  width: 100%;
  max-width: 100%;

  &__container {
    display: flex;
    flex-direction: column;
    gap: rem(24px);

    @include mq(medium) {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: stretch;

      // Con pochi servizi la colonna destra è bassa: senza un minimo
      // l'immagine (verticale) verrebbe schiacciata in una fascia sottile.
      min-height: rem(580px);
    }
  }

  &__image {
    width: 100%;

    // No padding as requested

    // L'altezza del blocco è guidata dalla colonna servizi:
    // l'immagine riempie la cella (stretch) senza forzarne l'altezza.
    @include mq(medium) {
      position: relative;
      height: 100%;
      overflow: hidden;
    }
  }

  &__img {
    min-width: 100%;
    display: block;
    object-fit: cover;

    @include mq(medium) {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
  }

  &__content {
    width: 100%; // Equally divide horizontal space
    padding-right: var(--grid-margin);
    padding-left: calc(var(--grid-gutter) / 2);

    @include mq($until: tablet) {
      padding: 0 var(--space-s);
    }
  }

  &__title {
    // BigText container
  }

  &__gap {
    height: 77px; // Exact 77px gap as requested

    @include mq($until: tablet) {
      height: var(--space-xl); // Smaller gap on mobile
    }
  }

  &__list {
    // ServiceList container
  }
}
</style>
